import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';

import {
  MissingPriceConfigurationReasonCode,
  MissingPriceConfigurationReportItem
} from '../../../core/models/missing-price-configuration-report.model';
import { TableUserManualConfig } from '../../../core/models/catalog-table.model';
import { MissingPriceConfigurationsReportService } from '../../../core/services/missing-price-configurations-report.service';
import { UserManualWindowService } from '../../../shared/services/user-manual-window.service';

const REASON_LABELS: Record<MissingPriceConfigurationReasonCode, string> = {
  NO_CONFIGURATIONS_FOUND: 'Ausencia Total',
  GAP_BETWEEN_CONFIGURATIONS: 'Salto de Vigencia'
};

interface MissingPriceConfigurationReportRowView {
  reason_label: string;
  commercial_zone_name: string;
  commercial_condition_name: string;
  timezone_name: string;
  gap_start_date_view: string;
  gap_end_date_view: string;
}

@Component({
  selector: 'app-missing-price-configurations-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DatePickerModule,
    FloatLabelModule,
    MessageModule,
    TableModule,
    TooltipModule
  ],
  templateUrl: './missing-price-configurations-report.component.html',
  styleUrl: './missing-price-configurations-report.component.scss'
})
export class MissingPriceConfigurationsReportComponent {
  private readonly userManualWindowService = inject(UserManualWindowService);

  readonly columns = [
    { field: 'reason_label', header: 'Motivo' },
    { field: 'commercial_zone_name', header: 'Zona Comercial' },
    { field: 'commercial_condition_name', header: 'Condición Comercial' },
    { field: 'timezone_name', header: 'Zona Horaria' },
    { field: 'gap_start_date_view', header: 'Inicio del Hueco' },
    { field: 'gap_end_date_view', header: 'Fin del Hueco' }
  ];

  readonly missingPricesUserManual: TableUserManualConfig = {
    title: 'Manual de Ayuda: Precios Faltantes',
    embedUrl: 'https://scribehow.com/embed/Precios_Faltantes__PNK6I57qTUq_c6cm9Px3ow?skipIntro=true&as=video'
  };

  startDate = signal<Date | null>(null);
  endDate = signal<Date | null>(null);

  loading = signal(false);
  hasSearched = signal(false);
  rows = signal<MissingPriceConfigurationReportItem[]>([]);

  tableData = computed<MissingPriceConfigurationReportRowView[]>(() => {
    return this.rows()
      .map(row => ({
        reason_label: this.getReasonLabel(row.reason),
        commercial_zone_name: row.commercial_zone?.name || 'N/D',
        commercial_condition_name: row.commercial_condition?.name || 'N/D',
        timezone_name: row.timezone?.name || 'N/D',
        gap_start_date_view: this.formatDateForView(row.gap_start_date),
        gap_end_date_view: this.formatDateForView(row.gap_end_date)
      }));
  });

  validationError = signal('');
  requestError = signal('');

  minEndDate = computed<Date | undefined>(() => {
    const start = this.startDate();
    if (!start) {
      return undefined;
    }

    const min = new Date(start);
    min.setHours(0, 0, 0, 0);
    return min;
  });

  constructor(private reportService: MissingPriceConfigurationsReportService) {}

  openUserManual(): void {
    this.userManualWindowService.open(this.missingPricesUserManual);
  }

  onStartDateChange(value: Date | null): void {
    this.startDate.set(value);
    this.validationError.set('');
    this.requestError.set('');

    if (!value) {
      this.hideResultsUntilNextQuery();
      return;
    }

    const endDate = this.endDate();
    if (!endDate) {
      return;
    }

    if (this.normalizeDate(endDate).getTime() < this.normalizeDate(value).getTime()) {
      this.endDate.set(null);
      this.hideResultsUntilNextQuery();
    }
  }

  onEndDateChange(value: Date | null): void {
    this.endDate.set(value);
    this.validationError.set('');
    this.requestError.set('');

    if (!value) {
      this.hideResultsUntilNextQuery();
    }
  }

  generateReport(): void {
    if (!this.validateDateRange()) {
      return;
    }

    const startDate = this.formatDateForApi(this.startDate() as Date);
    const endDate = this.formatDateForApi(this.endDate() as Date);

    this.loading.set(true);
    this.hasSearched.set(false);
    this.requestError.set('');
    this.rows.set([]);

    this.reportService.getReport(startDate, endDate).subscribe({
      next: (responseRows) => {
        const sortedRows = [...responseRows].sort((a, b) => this.compareIsoDates(a.gap_start_date, b.gap_start_date));
        this.rows.set(sortedRows);
        this.hasSearched.set(true);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.hasSearched.set(false);
        this.loading.set(false);
        this.requestError.set('No se pudo generar el reporte. Intente nuevamente.');
      }
    });
  }

  getReasonLabel(reason: string): string {
    const normalizedReason = this.normalizeReasonCode(reason);

    if (normalizedReason === 'NO_CONFIGURATIONS_FOUND' || normalizedReason === 'GAP_BETWEEN_CONFIGURATIONS') {
      return REASON_LABELS[normalizedReason];
    }

    return this.formatUnknownReason(reason);
  }

  formatDateForView(value: string): string {
    const parsedDate = this.parseApiDate(value);
    if (!parsedDate) {
      return value;
    }

    return parsedDate.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  private validateDateRange(): boolean {
    this.validationError.set('');

    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!startDate || !endDate) {
      this.validationError.set('Debe seleccionar la Fecha de Inicio y la Fecha de Fin.');
      return false;
    }

    if (this.normalizeDate(endDate).getTime() < this.normalizeDate(startDate).getTime()) {
      this.validationError.set('La Fecha de Fin no puede ser anterior a la Fecha de Inicio.');
      return false;
    }

    return true;
  }

  private formatDateForApi(date: Date): string {
    const normalizedDate = this.normalizeDate(date);
    const year = normalizedDate.getFullYear();
    const month = (normalizedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = normalizedDate.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private parseApiDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const datePart = value.split('T')[0];
    const [yearStr, monthStr, dayStr] = datePart.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);

    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  private normalizeDate(value: Date): Date {
    const normalizedDate = new Date(value);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate;
  }

  private compareIsoDates(leftDate: string, rightDate: string): number {
    const leftTimestamp = Date.parse(leftDate);
    const rightTimestamp = Date.parse(rightDate);

    if (Number.isNaN(leftTimestamp) && Number.isNaN(rightTimestamp)) {
      return 0;
    }

    if (Number.isNaN(leftTimestamp)) {
      return 1;
    }

    if (Number.isNaN(rightTimestamp)) {
      return -1;
    }

    return leftTimestamp - rightTimestamp;
  }

  private normalizeReasonCode(reason: string): string {
    return (reason || '').trim().toUpperCase();
  }

  private hideResultsUntilNextQuery(): void {
    this.hasSearched.set(false);
    this.rows.set([]);
  }

  clearFilters(): void {
    this.startDate.set(null);
    this.endDate.set(null);
    this.validationError.set('');
    this.requestError.set('');
    this.hasSearched.set(false);
    this.rows.set([]);
  }

  private formatUnknownReason(reason: string): string {
    const cleanReason = (reason || '').trim();
    if (!cleanReason) {
      return 'Sin motivo';
    }

    return cleanReason
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
  }
}
